import React, { useContext, useState, useRef, useEffect } from "react";
import { ChatContext } from "../ChatContextProvider";
import dayjs from "dayjs";
import relativeTime from "dayjs/plugin/relativeTime";
import CustomImage from "../CustomImage";
import {
  ArrowRightIcon,
  ChevronLeft,
  MessageSquareDiff,
  MessagesSquare,
  X,
} from "lucide-react";
import { getConversations } from "../../services";

dayjs.extend(relativeTime);

const ConversationHistory = () => {
  const { isMobile } = useMobile();
  const [loading, setLoading] = useState(true);
  const [conversations, setConversations] = useState<any>([]);
  const { appState, setAppState, setCurrentConversation, goToConversation, handleWindowClose } =
    useContext(ChatContext);

  const moveBack = () => {
    setAppState({
      ...appState,
      currentScreen: "home",
      previousScreen: appState.currentScreen,
    });
    setCurrentConversation(null);
  };

  useEffect(() => {
    const fetchConversations = async () => {
      try {
        console.log("fetching conversations");
        setLoading(true);
        const resp = await getConversations({
          mx_uid: appState.mx_uid,
          tid: appState.tid,
          id: appState.id,
        });
        setConversations(resp);
        setLoading(false);
      } catch (e) {
        setConversations([]);
        setLoading(false);
      }
    };
    if (appState?.mx_uid) {
      fetchConversations();
    }
  }, [appState?.mx_uid]);

  if (loading) {
    return (
      <div
        className={`relative flex flex-col h-full w-full bg-background overflow-hidden border ${
          isMobile ? "rounded-none" : "rounded-xl"
        }`}
      >
        <div className="p-4 border-b text-gray-700 flex justify-between items-center">
          <div className="flex items-center">
            <button
              onClick={() => moveBack()}
              className="hover:bg-gray-300 rounded-lg p-2 outline-none focus:outline-none"
            >
              <ChevronLeft className="h-5 w-5" />
            </button>
            <span className="font-semibold">Conversations</span>
          </div>
          <div className="flex items-center gap-4">
            <button
              onClick={() => goToConversation({})}
              className="text-2xl leading-none focus:outline-none"
              aria-label="New conversation"
            >
              <MessageSquareDiff className="size-5" />
            </button>
            <button
              onClick={()=>handleWindowClose()}
              className="text-2xl leading-none focus:outline-none"
              aria-label="Close Chat"
            >
              <X className="size-5" />
            </button>
          </div>
        </div>
        <div className="flex-1 p-3 overflow-y-auto overflow-x-hidden">
          <div className="h-40 flex justify-center items-center">
            <svg
              aria-hidden="true"
              className="size-10 animate-spin text-secondary-500"
              viewBox="0 0 100 101"
              fill="none"
              xmlns="http://www.w3.org/2000/svg"
            >
              <path
                d="M100 50.5908C100 78.2051 77.6142 100.591 50 100.591C22.3858 100.591 0 78.2051 0 50.5908C0 22.9766 22.3858 0.59082 50 0.59082C77.6142 0.59082 100 22.9766 100 50.5908ZM9.08144 50.5908C9.08144 73.1895 27.4013 91.5094 50 91.5094C72.5987 91.5094 90.9186 73.1895 90.9186 50.5908C90.9186 27.9921 72.5987 9.67226 50 9.67226C27.4013 9.67226 9.08144 27.9921 9.08144 50.5908Z"
                fill="#e5e5e5"
              />
              <path
                d="M93.9676 39.0409C96.393 38.4038 97.8624 35.9116 97.0079 33.5539C95.2932 28.8227 92.871 24.3692 89.8167 20.348C85.8452 15.1192 80.8826 10.7238 75.2124 7.41289C69.5422 4.10194 63.2754 1.94025 56.7698 1.05124C51.7666 0.367541 46.6976 0.446843 41.7345 1.27873C39.2613 1.69328 37.813 4.19778 38.4501 6.62326C39.0873 9.04874 41.5694 10.4717 44.0505 10.1071C47.8511 9.54855 51.7191 9.52689 55.5402 10.0491C60.8642 10.7766 65.9928 12.5457 70.6331 15.2552C75.2735 17.9648 79.3347 21.5619 82.5849 25.841C84.9175 28.9121 86.7997 32.2913 88.1811 35.8758C89.083 38.2158 91.5421 39.6781 93.9676 39.0409Z"
                fill="currentColor"
              />
            </svg>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div
      className={`relative flex flex-col h-full w-full bg-background overflow-hidden border ${
        isMobile ? "rounded-none" : "rounded-xl"
      }`}
    >
      {/* Header */}
      <div className="p-4 border-b text-gray-700 flex justify-between items-center">
        <div className="flex items-center">
          <button
            onClick={moveBack}
            className="hover:bg-gray-300 rounded-lg p-2 outline-none focus:outline-none"
          >
            <ChevronLeft className="h-5 w-5" />
          </button>
          <span className="font-semibold ml-2">Conversations</span>
        </div>
        <div className="flex items-center gap-4">
          <button
            onClick={() => goToConversation({ conversation_purpose: "general" })}
            className="text-2xl leading-none focus:outline-none"
            aria-label="New conversation"
          >
            <MessageSquareDiff className="size-5" />
          </button>
          <button
            onClick={()=>handleWindowClose()}
            className="text-2xl leading-none focus:outline-none"
            aria-label="Close Chat"
          >
            <X className="size-5" />
          </button>
        </div>
      </div>

      {/* Conversations List */}
      <div className="overflow-y-auto">
        {conversations?.map((data: any) => (
          <div
            key={data?.conversation.id}
            className="flex items-start justify-between bg-white hover:bg-gray-50 border-b p-4 cursor-pointer"
            onClick={() => goToConversation({ cid: data?.conversation?.uid })}
          >
            <div className="flex flex-1 items-start space-x-3 min-w-0">
              <div>
                <CustomImage
                  src={data?.lastMessage?.metadata?.avatar_url}
                  alt={data?.lastMessage?.first_name}
                  size={38}
                />
              </div>
              <div className="flex flex-col w-full">
                <p className="font-medium text-xs text-gray-500">
                  {data?.lastMessage?.metadata?.first_name}
                </p>
                <p
                  className={`text-black text-sm line-clamp-2 ${
                    data?.conversation.unread > 0 && "font-medium"
                  }`}
                >
                  {data?.lastMessage?.content}
                </p>
              </div>
            </div>

            {/* Date */}
            <div className="flex flex-col items-end ml-4 mt-1">
              <p className="text-xs text-gray-500">
                {dayjs(data?.lastMessage?.createdAt).fromNow()}
              </p>
            </div>
          </div>
        ))}
      </div>
      {conversations.length === 0 && (
        <div className="mx-auto h-72 flex flex-col gap-4 justify-center items-center">
          <MessagesSquare className="size-10" />
          <p className="text-sm font-base">
            No conversations. Let's start a new one
          </p>
          <div
            className="flex flex-col w-full items-start px-4 py-3 rounded-xl shadow bg-gray-50 hover:bg-gray-100 cursor-pointer space-y-2"
            onClick={() => goToConversation({ conversation_purpose: "general" })}
          >
            <div className="flex flex-1 flex-row w-full">
              <div className="flex-1 flex justify-between items-center truncate">
                <div className="flex flex-col w-full">
                  <p className="truncate text-ellipsis">
                    {"Send us a message"}
                  </p>
                </div>
                <ArrowRightIcon className="bg-gray-200 rounded-full h-8 w-8 p-1.5" />
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

const useMobile = () => {
  const [isMobile, setMobile] = useState(false);

  useEffect(() => {
    const applyResponsiveClasses = () => {
      if (window.parent) {
        const parentWidth = window.parent.innerWidth;

        if (parentWidth < 768) {
          setMobile(true);
        } else {
          setMobile(false);
        }
      }
    };

    applyResponsiveClasses();
    window.parent.addEventListener("resize", applyResponsiveClasses);

    return () => {
      window.parent.removeEventListener("resize", applyResponsiveClasses);
    };
  }, []);

  return { isMobile };
};

export default ConversationHistory;
